// src/api/healthcare/Testing/TestResultApi.ts

import { ENV } from '../../../config/runtimeEnv';
import { TestResult } from "../../../types/healthcare/TestTypes";
import { http } from "../../../lib/http/client";

const HEALTHCARE_BASE_URL = ENV.VITE_HEALTHCARE_SERVER;

export interface TestResultFilters {
	company: string;
	page_number: number;
	rows: number;
	search?: string;
	filters?: Record<string, any>;
	sort_field?: string;
	sort_order?: 'asc' | 'desc' | 'ascend' | 'descend';
}

// Get in-tray results (pending review) with filtering and sorting
export const fetchInTrayResults = async (
	filters: TestResultFilters
): Promise<{ data: TestResult[]; total: number }> => {
	const params = {
		page_number: filters.page_number,
		rows: filters.rows,
		search: filters.search,
		filters: filters.filters ? JSON.stringify(filters.filters) : undefined,
		sort_field: filters.sort_field,
		sort_order: filters.sort_order
	};

	const response = await http.get(`${HEALTHCARE_BASE_URL}/patients/test-results/in-tray`, {
		params,
	});

	return { data: response.data.data, total: response.data.total };
};

// Get a specific test result
export const fetchTestResult = async (testId: number, testType: string): Promise<TestResult> => {
	const response = await http.get(
		`${HEALTHCARE_BASE_URL}/patients/test-orders/${testId}/results/${testType}`
	);
	return response.data;
};

// Update a test result
export const updateTestResult = async (
	testId: number,
	testType: string,
	data: Partial<TestResult>
): Promise<TestResult> => {
	const response = await http.put(
		`${HEALTHCARE_BASE_URL}/patients/test-orders/${testId}/results/${testType}`,
		data
	);
	return response.data;
};

// Update test result status only
export const updateTestResultStatus = async (
	testId: number,
	testType: string,
	status: string,
	result_notes?: string
): Promise<TestResult> => {
	const response = await http.put(
		`${HEALTHCARE_BASE_URL}/patients/test-orders/${testId}/results/${testType}/status`,
		{ status, result_notes }
	);
	return response.data;
};

// Bulk update test results
export const bulkUpdateTestResults = async (
	updates: Array<{
		test_id: number;
		test_type: string;
		status?: string;
		is_abnormal?: boolean;
		requires_followup?: boolean;
		result_notes?: string;
	}>
): Promise<TestResult[]> => {
	const response = await http.patch(
		`${HEALTHCARE_BASE_URL}/patients/test-results/bulk-update`,
		{ updates }
	);
	return response.data;
};